import {
  ResearchFilter,
  BacteriaResearchSummary,
  ResearchCategory,
  Taxonomy,
} from '@snapi/types';
import { useToggle } from 'react-use';
import { ReportResearchFilter } from './report-research-filter';
import { useState, useMemo } from 'react';
import { AnimateSharedLayout, motion } from 'framer-motion';
import { Button, ClueCard, Percentage, Subtitle } from '@snapi/luxury';
import { FilterIcon } from '@snapi/icons';
import { ReportResearchFilterModal } from './report-research-filter-modal';
import { BacteriaResearchSummaryCard } from '../bacteria-research-summary-card';
import { ResearchFilterUtils } from '@snapi/utils';
import { useReportContext } from '../report-context';
import { ResearchCategoryToBackgroundColor } from '@snapi/ui-utils';
import clsx from 'clsx';
import { ReportHero } from '../report-hero';

const CardVariants = {
  shown: {
    height: 'auto',
    opacity: 1,
    marginBottom: 12,
  },
  hidden: {
    height: 0,
    opacity: 0,
  },
};

interface ReportBacteriaListProps {
  summary: BacteriaResearchSummary[];
  other: number;
}

export const ReportBacteriaList: React.FC<ReportBacteriaListProps> = ({
  summary,
  other,
}) => {
  const { baby } = useReportContext();

  const [filter, setFilter] = useState<ResearchFilter>({
    categories: [],
    conditions: [],
  });

  const summaryForRendering = useMemo(() => {
  return summary
    .map((item) => {
      const bacteria = item.bacteria;

      let newPercentage;
      if (
        bacteria.research.taxonomy === Taxonomy.family &&
        ResearchFilterUtils.filterResearch(item.bacteria, filter)
      ) {
        newPercentage = bacteria.other + bacteria.unclassified;
      }
      else {
          newPercentage = ResearchFilterUtils.overlapPercentage(
          bacteria,
          item.species,
          filter
        );
      }

      const newItem = {
        ...item,
        bacteria: {
          ...item.bacteria,
          percentage: newPercentage,
        },
      };
        return newItem;
    })
   .sort((a, b) => b.bacteria.percentage - a.bacteria.percentage)
  }, [summary, filter]);
  

  const conditions = useMemo(() => {
    const allConditions = summary.flatMap(
      ({
        bacteria: {
          research: { condition },
        },
        species,
      }) =>
        condition.concat(
          species.flatMap(({ research: { condition } }) => condition)
        )
    );
    const uniqueConditions = Array.from(new Set(allConditions));
    const sortedConditions = uniqueConditions.sort((a, b) =>
      a.localeCompare(b)
    );

    return sortedConditions;
  }, [summary]);

  const [showAll, toggleShowAll] = useToggle(false);

  const shownCards: string[] = useMemo(() => {
    if (filter.categories.length === 0 && filter.conditions.length === 0) {
      return summary.map(
        ({
          bacteria: {
            research: { name },
          },
        }) => name
      )
    }

    const filtered = summary.filter(
      ({ bacteria, species: children }) =>
        ResearchFilterUtils.overlapPercentage(bacteria, children, filter) > 0
    );

    return filtered.map(
      ({
        bacteria: {
          research: { name },
        },
      }) => name
    );
  }, [summary, filter]);

  const filteredCards: Set<string> = useMemo(() => {
    if (showAll) {
      return new Set<string>(shownCards);
    }
    return new Set<string>(shownCards.slice(0, 5));
  }, [shownCards, showAll]);

  const [selected, setSelected] = useState<null | string>(null);

  const showOther = useMemo(
    () =>
      other > 0 &&
      ResearchFilterUtils.filterResearch(
        { research: { category: ResearchCategory.other, condition: [] } },
        filter
      ),
    [other, filter]
  );

  const handleSelect = (name: string) => () => {
    if (selected === name) {
      setSelected(null);
    } else {
      setSelected(name);
    }
  };

  return (
    <section className="flex flex-col items-center pb-0">
      <div className="flex flex-col gap-5 container mb-10 lg:flex-row lg:gap-8 text-blue-dark">
        <h2 className="flex lg:basis-1/2">Raw Data</h2>
        <p className="flex lg:basis-1/2">
          The bacteria guide is where you can learn detailed information
          regarding your baby’s microbiome test. Try sorting by disruptive
          bacteria to see which bacteria you want to consider decreasing.
          Worried about eczema? Check out the eczema association to learn more
          about the bacteria related to this condition.
        </p>
      </div>

      <div className="container flex flex-col gap-10 md:gap-20">
        <div className="flex flex-1 flex-col md:flex-row">
          <div className="hidden lg:flex flex-1 flex-col">
            <div className="flex pr-9 pb-2 border-b border-blue-dark md:sticky">
              <span className="mr-3 text-blue-dark">
                <FilterIcon />
              </span>
              <span className="flex self-center text-blue-dark">
                <Subtitle>Filter</Subtitle>
              </span>
            </div>
            <div className="my-5">
              <ReportResearchFilter
                conditions={conditions}
                filter={filter}
                onChange={setFilter}
              />
            </div>
          </div>
          <div className="flex flex-[2] flex-col justify-center">
            <div className="flex flex-1 pb-2 mb-5 border-b border-blue-dark justify-center lg:justify-end">
              <div className="flex min-w-[290px] flex-1 max-w-[610px] xl:max-w-[630px] px-3">
                <div className="flex flex-[3] sm:flex-1 items-center relative text-blue-dark">
                  <Subtitle>Bacteria name</Subtitle>
                  <span className="ml-3 flex items-center">
                    <ClueCard
                      orientation="bottom"
                      title="Scientific classification"
                      description="Here you’ll find the scientific name of the bacteria and percentage of species detected in the microbiome"
                    />
                  </span>
                </div>
                <div className="flex flex-1 items-center relative text-blue-dark">
                  <Subtitle>
                    <span className="sm:flex hidden">Percentage (%)</span>
                    <span className="sm:hidden flex ml-2">%</span>
                  </Subtitle>
                </div>
                <div className="flex w-4 items-center lg:invisible">
                  <ReportResearchFilterModal
                    conditions={conditions}
                    filter={filter}
                    onChange={setFilter}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-1 justify-center lg:justify-end relative">
              <div className="flex flex-1 flex-col w-[290px] xs:w-full max-w-[610px] xl:max-w-[630px] relative">
                {summary.map((summary) => {
                  return (
                    <motion.div
                      key={summary.bacteria.research.name}
                      layout
                      animate={
                        filteredCards.has(summary.bacteria.research.name)
                          ? 'shown'
                          : 'hidden'
                      }
                      transition={{ duration: 0.3 }}
                      variants={CardVariants}
                    >
                      <BacteriaResearchSummaryCard
                        bacteria={summary.bacteria}
                        species={summary.species}
                        filter={filter}
                        expanded={selected === summary.bacteria.research.name}
                        onExpand={handleSelect(summary.bacteria.research.name)}
                      />
                    </motion.div>
                  );
                })}
                <motion.div
                  layout
                  animate={showOther ? 'shown' : 'hidden'}
                  transition={{ duration: 0.3 }}
                  variants={CardVariants}
                >
                  <div className="bg-white rounded-xl py-4 px-3">
                    <div className="flex">
                      {/* <span
                          className={clsx(
                            'h-4 w-4 flex self-center rounded-full',
                            ResearchCategoryToBackgroundColor.other
                          )}
                        /> */}
                      <div className="flex">
                        <h5 className="opacity-50">Other</h5>
                        <h5 className="opacity-50 ml-[140px] sm:ml-[220px] md:ml-[236px] xl:ml-[244px]">
                          {' '}
                          <Percentage percentage={other} />
                        </h5>
                      </div>
                    </div>
                  </div>
                </motion.div>
                <motion.div
                  layout
                  animate={shownCards.length > 6 ? 'shown' : 'hidden'}
                  transition={{ duration: 0.3 }}
                  variants={CardVariants}
                  className="flex flex-1 justify-center mt-2 mb-4 z-10 self-center"
                >
                  <Button size="md" onClick={toggleShowAll}>
                    {showAll ? 'Hide' : 'See'} all {`(${shownCards.length})`}
                  </Button>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container flex flex-1 justify-center">
        <div className="flex flex-1">
          <div className="border-t border-blue-dark flex flex-1 pt-[75px]"></div>
        </div>
      </div>
    </section>
  );
};
